import { yupResolver } from '@hookform/resolvers/yup'
import { Close } from '@mui/icons-material'
import {
  CardActions,
  CardContent,
  Divider,
  Grid,
  Typography
} from '@mui/material'
import {
  Button,
  Card,
  CardHeader,
  IconButton,
  Modal,
  SelectC,
  TextFieldC
} from 'components'
import {
  useCreateOrderTracking,
  useLoadOrderTrackingTitles
} from 'modules/orders/hooks'
import { FormProvider, useForm } from 'react-hook-form'
import { Yup } from 'utils'
import { Attachments } from 'modules/orders/components/Tracking/Attachments'
import { FileUploadProvider, useFiles } from 'contexts'
import fileSize from 'filesize'
import { ATTACHMENT_SIZE_LIMIT } from 'config'
import { useTrackingModalStore } from './trackingModalStore'
import { useEffect } from 'react'

type FormData = {
  title: { id: string; name: string }
  subject: string
  datePlanned: string
  dateHeld: string
  text: string
  emailCc: { id: string; value: string }[]
  email: { id: string; value: string }[]
  sendEmail: boolean
}

const schema = Yup.object({
  title: Yup.object({
    id: Yup.string().required()
  }),
  subject: Yup.string().required(),
  text: Yup.string()
})

function TrackingProvided() {
  const { uploadedFiles: trackingFiles } = useFiles()
  const orderData = useTrackingModalStore.useData()
  const open = useTrackingModalStore.useIsModalOpen()
  const setModalOpen = useTrackingModalStore.useSetModalOpen()

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      title: null,
      subject: `Acompanhamento Web: Pedido: ${orderData.orderId}`,
      datePlanned: null,
      dateHeld: null,
      text: '',
      email: [],
      emailCc: [],
      sendEmail: false
    }
  })

  const { handleSubmit, reset, watch } = methods

  const { data: titles } = useLoadOrderTrackingTitles({})

  const { mutateAsync, isLoading } = useCreateOrderTracking()

  const onSubmit = async (data: FormData) => {
    try {
      const normalizedData = {
        files: trackingFiles.map((file) => file.path),
        orderId: orderData.orderId,
        customerId: orderData.customerId,
        subject: data.subject,
        text: data.text,
        titleId: data.title.id,
        emailCc: data.emailCc.map((email) => email.value),
        email: data.email.map((email) => email.value),
        sendEmail: data.sendEmail,
        sequence: orderData?.item?.sequence,
        type: orderData.type
        // dateHeld: new Date(data.datePlanned).toISOString(),
        // datePlanned: new Date(data.datePlanned).toISOString()
      }
      await mutateAsync(normalizedData)
      setModalOpen(false)
      reset()
    } catch {}
  }

  const uploading = trackingFiles.some(
    (item) => (item?.progress || 0) < 100 && !item.uploaded
  )

  const isNotUploadedItem = trackingFiles.some((item) => !item.uploaded)
  const totalSize = trackingFiles.reduce(
    (acc, cur) => acc + (cur?.file?.size || 0),
    0
  )

  const isExceededSizeLimit = totalSize >= ATTACHMENT_SIZE_LIMIT

  useEffect(() => {
    if (orderData?.subject) {
      methods.setValue('subject', orderData.subject)
      methods.setValue('title', orderData.title)
    }
  }, [orderData, methods])

  return (
    <FormProvider {...methods}>
      <Modal
        open={open}
        onClose={() => setModalOpen(false)}
        keepMounted={false}
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)'
        }}
      >
        <Card style={{ padding: 0, minWidth: 300, width: 600 }}>
          <CardHeader
            title={`Acompanhamento ${orderData.orderId}`}
            HeaderRight={
              <IconButton onClick={() => setModalOpen(false)}>
                <Close />
              </IconButton>
            }
          />
          <Divider />

          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <SelectC
                  name="title"
                  options={titles?.data}
                  label="Título"
                  disabled={orderData.title}
                  getOptionLabel={(option: any) => option?.name}
                  loading={false}
                />
              </Grid>

              {/* <Grid item xs={12} sm={6}>
                <DatePickerC
                  name="datePlanned"
                  label="Data Planejada"
                  inputFormat="dd/MM/yyyy"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePickerC
                  name="dateHeld"
                  label="Data Realizada"
                  inputFormat="dd/MM/yyyy"
                />
              </Grid> */}
              <Grid item xs={12} sm={12}>
                <TextFieldC name="subject" label="Assunto" disabled />
              </Grid>
              <Grid item xs={12}>
                <TextFieldC name="text" label="Texto" multiline rows={4} />
              </Grid>
              <Grid item xs={12}>
                <Attachments limitFiles={10} />
              </Grid>
              {isExceededSizeLimit && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="error">
                    A soma dos tamanhos de cada arquivo deve ser menor que{' '}
                    {fileSize(ATTACHMENT_SIZE_LIMIT)}!
                  </Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
          <Divider />
          <CardActions>
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                gap: '1rem'
              }}
            >
              <Button
                variant="text"
                sx={{
                  width: { sm: 100, md: 200 }
                }}
                onClick={() => setModalOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                sx={{
                  width: { sm: 100, md: 200 }
                }}
                type="button"
                disabled={
                  uploading ||
                  isNotUploadedItem ||
                  isLoading ||
                  isExceededSizeLimit
                }
                onClick={handleSubmit(onSubmit)}
              >
                Cadastrar {watch('sendEmail') ? 'e enviar email' : ''}
              </Button>
            </div>
          </CardActions>
        </Card>
      </Modal>
    </FormProvider>
  )
}

const Tracking = () => (
  <FileUploadProvider>
    <TrackingProvided />
  </FileUploadProvider>
)

export default Tracking
